import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, GoogleLogo, SpeakerSlash, SpeakerHigh } from "@phosphor-icons/react";
import { CLINIC, IMAGES, TEAM, REVIEWS, WHY_US } from "../data/site";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { MarqueeStrip } from "../components/MarqueeStrip";
import { AccreditationStrip } from "../components/AccreditationStrip";
import { CTABand } from "../components/CTABand";
import { EnquiryDialog } from "../components/EnquiryDialog";

const CHAPTERS = [
  {
    n: "01",
    title: "We listen first",
    text: "Dee founded this clinic after her own battle with Melasma — years of feeling unheard. That's why the consultation is the most vital part of your journey: your story comes before any syringe.",
  },
  {
    n: "02",
    title: "Science over trends",
    text: "With backgrounds in Human Genetics, Medical Sciences and an MSc in Skin Ageing, we look past the surface — cellular health, structural regeneration and evidence-based protocols only.",
  },
  {
    n: "03",
    title: "Naturally, you",
    text: "Safety, precision and natural-looking results. We enhance your confidence and wellbeing — never mask who you are. Be true, be you.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.volume = 1;
    setMuted(v.muted);
  };

  const videoSrc =
    typeof window !== "undefined" && window.innerWidth < 640 ? IMAGES.videoMobile : IMAGES.videoHd;

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-foreground" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.heroPoster}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl sm:hidden"
        />
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full scale-[1.25] object-contain [transform-origin:50%_55%] sm:scale-100 sm:object-cover sm:[transform-origin:center]"
          src={videoSrc}
          poster={IMAGES.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/25" />
      <button
        onClick={toggleSound}
        data-testid="hero-sound-toggle"
        aria-label={muted ? "Turn sound on" : "Turn sound off"}
        className="absolute right-5 top-24 z-20 flex items-center gap-2.5 border border-white/30 bg-black/30 px-4 py-2.5 text-[10px] font-medium uppercase tracking-[0.25em] text-white backdrop-blur-sm transition-colors duration-300 hover:bg-black/50 sm:right-8"
      >
        {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
        {muted ? "Sound off" : "Sound on"}
      </button>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 sm:px-8">
        <MaskedLine delay={0.25}>
          <span className="text-[11px] font-medium uppercase tracking-[0.4em] text-white/70 sm:text-xs">
            {CLINIC.tagline} — Harley Street Trained
          </span>
        </MaskedLine>
        <h1 className="mt-6 font-display text-[13.5vw] font-light leading-[0.95] tracking-tight text-white sm:text-8xl lg:text-[7.5rem]">
          <MaskedLine delay={0.4}>Where science</MaskedLine>
          <MaskedLine delay={0.55}>
            <span>
              meets <em className="text-primary-foreground/90 not-italic underline decoration-primary decoration-[3px] underline-offset-[12px]">skin.</em>
            </span>
          </MaskedLine>
        </h1>
        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <MaskedLine delay={0.75}>
            <span className="max-w-md text-base font-light leading-relaxed text-white/80">
              Advanced facial aesthetics, skin rejuvenation and longevity medicine in Draycott, Derby —
              led by registered dental professional Dee Kaur.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-3 sm:flex-row"
          >
            <EnquiryDialog
              defaultType="appointment"
              trigger={
                <button
                  data-testid="hero-book-appointment-button"
                  className="group flex items-center justify-center gap-2 bg-primary px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/85"
                >
                  Book appointment
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </button>
              }
            />
            <EnquiryDialog
              defaultType="consultation"
              trigger={
                <button
                  data-testid="hero-free-consultation-button"
                  className="border border-white/40 px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
                >
                  Free consultation
                </button>
              }
            />
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-10 flex items-center gap-3 border-t border-white/15 pt-6"
          data-testid="hero-rating"
        >
          <GoogleLogo size={20} weight="fill" className="text-white/80" />
          <span className="flex gap-0.5 text-white">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={15} weight="fill" />
            ))}
          </span>
          <span className="text-sm font-light text-white/70">
            {CLINIC.rating} · {CLINIC.reviewCount} Google reviews
          </span>
        </motion.div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="manifesto-section">
    <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
      <div className="md:col-span-5">
        <Reveal>
          <Eyebrow>The DK Philosophy</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
            A clinic built on <em className="text-primary">being heard.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative mt-10 overflow-hidden">
            <motion.img
              src={IMAGES.dee}
              alt="Dee Kaur, founder of DK Aesthetics"
              data-testid="manifesto-dee-image"
              className="aspect-[3/4] w-full object-cover"
              initial={{ scale: 1.12 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="absolute bottom-0 left-0 bg-background px-5 py-3">
              <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Dee Kaur · Founder</p>
            </div>
          </div>
        </Reveal>
      </div>
      <div className="flex flex-col justify-center gap-0 md:col-span-7">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={0.1 * i}>
            <div className="group flex gap-8 border-t border-border py-10 transition-colors duration-500 last:border-b hover:bg-secondary/40" data-testid={`manifesto-chapter-${c.n}`}>
              <span className="font-display text-5xl font-light italic text-primary/25 transition-colors duration-500 group-hover:text-primary sm:text-6xl">
                {c.n}
              </span>
              <div>
                <h3 className="font-display text-2xl font-light tracking-tight sm:text-3xl">{c.title}</h3>
                <p className="mt-3 max-w-lg text-sm font-light leading-relaxed text-muted-foreground sm:text-base">{c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="why-us-section">
    <Reveal>
      <Eyebrow>Why choose us</Eyebrow>
      <h2 className="mt-4 font-display text-4xl font-light tracking-tight sm:text-5xl">
        Care that feels <em className="text-primary">personal.</em>
      </h2>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
      {WHY_US.map((w, i) => (
        <Reveal key={w.title} delay={0.08 * i} y={24} className="h-full">
          <div className="flex h-full flex-col bg-background p-8" data-testid={`why-us-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="font-display text-4xl font-light italic text-primary/30">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-6 font-display text-2xl font-light">{w.title}</h3>
            <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-muted-foreground">{w.text}</p>
            <Link to={w.link.href} className="group mt-6 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.25em] text-primary">
              {w.link.label}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const TeamPreview = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="team-preview-section">
    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <Reveal>
        <Eyebrow>Meet the team</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-light tracking-tight sm:text-5xl">
          The hands behind <em className="text-primary">your results.</em>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Link to="/team" data-testid="team-view-all-link" className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-primary">
          Full bios
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
    <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
      {TEAM.map((m, i) => (
        <Reveal key={m.id} delay={0.1 * i} y={30}>
          <Link to={`/team#${m.id}`} className="group block" data-testid={`team-card-${m.id}`}>
            <div className="overflow-hidden">
              {m.image ? (
                <img
                  src={m.image}
                  alt={m.name}
                  className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              ) : (
                <div className="flex aspect-[3/4] w-full items-center justify-center border border-primary/15 bg-accent/60">
                  <span className="font-display text-7xl font-light italic text-primary/40">
                    {m.name.replace("Dr. ", "").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-5 border-t border-border pt-4">
              <h3 className="font-display text-2xl font-light transition-colors duration-300 group-hover:text-primary">{m.name}</h3>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">{m.title}</p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Reviews = () => (
  <section className="border-y border-border bg-secondary/40 py-24 sm:py-32" data-testid="reviews-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="text-center">
        <div className="inline-flex items-center gap-3 border border-border bg-background px-6 py-3">
          <GoogleLogo size={20} weight="fill" className="text-primary" />
          <span className="flex gap-0.5 text-primary">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={15} weight="fill" />
            ))}
          </span>
          <span className="text-sm font-light">{CLINIC.rating} · {CLINIC.reviewCount} Google reviews</span>
        </div>
        <h2 className="mx-auto mt-8 max-w-2xl font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
          Loved by the people who <em className="text-primary">matter most.</em>
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-px bg-border md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={0.08 * i} y={24} className="h-full">
            <figure className="flex h-full flex-col bg-background p-8" data-testid={`review-card-${i}`}>
              <div className="flex gap-0.5 text-primary">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={13} weight="fill" />
                ))}
              </div>
              <blockquote className="mt-5 flex-1">
                <p className="font-display text-xl font-light italic leading-relaxed">"{r.text}"</p>
              </blockquote>
              <figcaption className="mt-6 border-t border-border/70 pt-4">
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.title}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-10 text-center">
          <a
            href="https://www.google.com/maps/search/DK+Aesthetics+DK+Cosmetic+Clinic+Draycott+Derby"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-primary"
          >
            Read all {CLINIC.reviewCount} reviews on Google
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const ClinicGlimpse = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="clinic-section">
    <div className="grid grid-cols-1 items-center gap-14 md:grid-cols-12">
      <Reveal className="md:col-span-7">
        <div className="grid grid-cols-2 gap-4">
          <img src={IMAGES.exterior} alt="DK Cosmetic Clinic, Collis House, Draycott" className="aspect-[4/5] w-full object-cover" data-testid="clinic-exterior-image" />
          <img src={IMAGES.treatmentRoom} alt="Treatment room at DK Aesthetics" className="mt-12 aspect-[4/5] w-full object-cover" data-testid="clinic-room-image" />
        </div>
      </Reveal>
      <div className="md:col-span-5">
        <Reveal>
          <Eyebrow>The clinic</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
            A calm space in the heart of <em className="text-primary">Draycott.</em>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted-foreground">
            A modern, welcoming clinic with easy parking right outside — designed so every visit feels
            considered, private and entirely about you.
          </p>
          <p className="mt-6 text-sm font-light text-foreground/80">{CLINIC.address}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <a
            href={CLINIC.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="clinic-directions-button"
            className="group mt-8 inline-flex items-center gap-2 border border-foreground px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            Get directions
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <AccreditationStrip />
      <MarqueeStrip />
      <Manifesto />
      <WhyUs />
      <TeamPreview />
      <Reviews />
      <ClinicGlimpse />
      <CTABand />
    </main>
  );
}
